// app/javascript/components/AdminMedia.jsx
'use client'
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Card, CardContent } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { DirectUpload } from '@rails/activestorage';
import { Progress } from "./ui/progress";

// CSRF Token Setup
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
if (csrfToken) {
    axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;
}

// Helper Functions
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime())
        ? 'N/A'
        : `${date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
};

const statusStyles = {
    queued: 'border-yellow-500 bg-yellow-100 text-yellow-800',
    generating: 'border-blue-500 bg-blue-100 text-blue-800',
    success: 'border-green-500 bg-green-100 text-green-800',
    failed: 'border-red-500 bg-red-100 text-red-800',
    deleted: 'border-gray-500 bg-gray-100 text-gray-800'
};

const AdminMedia = ({ initialMedia, pagy }) => {
    const [mediaItems, setMediaItems] = useState(initialMedia || []);
    const [page, setPage] = useState(pagy?.page || 1);
    const [hasMore, setHasMore] = useState(!!pagy?.next);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState({});
    const [editStates, setEditStates] = useState({}); // { mediaId: { status, generatedImageFile, failureReason, uploadProgress } }

    const fetchMedia = useCallback(async (pageNum) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`/admin/media.json?page=${pageNum}`);
            const { media, pagy } = response.data;
            setMediaItems((prev) => (pageNum === 1 ? media || [] : [...prev, ...(media || [])]));
            setHasMore(!!pagy.next);
        } catch (err) {
            setError('Failed to load media.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMedia(page);
    }, [page, fetchMedia]);

    const handleEditChange = (mediaId, field, value) => {
        setEditStates((prev) => ({
            ...prev,
            [mediaId]: {
                ...prev[mediaId],
                [field]: value
            }
        }));
    };

    const handleGeneratedImageUpload = async (mediaId, file) => {
        if (!file) return;

        setEditStates((prev) => ({
            ...prev,
            [mediaId]: { ...prev[mediaId], uploadProgress: 0 }
        }));

        try {
            const upload = new DirectUpload(file, '/rails/active_storage/direct_uploads', {
                directUploadWillStoreFileWithXHR: (xhr) => {
                    xhr.upload.addEventListener('progress', (event) => {
                        if (event.lengthComputable) {
                            const percent = Math.round((event.loaded / event.total) * 100);
                            setEditStates((prev) => ({
                                ...prev,
                                [mediaId]: { ...prev[mediaId], uploadProgress: percent }
                            }));
                        }
                    });
                }
            });

            const { signed_id } = await new Promise((resolve, reject) => {
                upload.create((error, blob) => {
                    if (error) reject(error);
                    else resolve(blob);
                });
            });

            handleEditChange(mediaId, 'generated_image', signed_id);
        } catch (err) {
            setError('Failed to upload generated image.');
            console.error(err);
        }
    };

    const saveChanges = async (mediaId) => {
        setActionLoading((prev) => ({ ...prev, [`save_${mediaId}`]: true }));
        try {
            const editData = editStates[mediaId] || {};
            const response = await axios.put(`/media/${mediaId}.json`, {
                media: {
                    status: editData.status || mediaItems.find(m => m.id === mediaId).status,
                    generated_image: editData.generated_image,
                    failure_reason: editData.failure_reason
                }
            });

            setMediaItems((prev) =>
                prev.map((item) =>
                    item.id === mediaId ? { ...item, ...response.data } : item
                )
            );
            setEditStates((prev) => {
                const { [mediaId]: _, ...rest } = prev;
                return rest;
            });
        } catch (err) {
            setError('Failed to update media.');
            console.error(err);
        } finally {
            setActionLoading((prev) => ({ ...prev, [`save_${mediaId}`]: false }));
        }
    };

    const cancelChanges = (mediaId) => {
        setEditStates((prev) => {
            const { [mediaId]: _, ...rest } = prev;
            return rest;
        });
    };

    const loadMore = () => hasMore && !loading && setPage((prev) => prev + 1);

    return (
        <div className="min-h-screen bg-pink-50 p-4 lg:p-8 font-nunito">
            <h1 className="text-3xl lg:text-4xl font-black mb-6 px-6 py-2">
                Admin Media Management
            </h1>

            {error && (
                <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-xl border-2 border-red-500">{error}</div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading && mediaItems.length === 0 ? (
                    Array.from({ length: 3 }).map((_, index) => (
                        <Skeleton key={index} className="w-full h-[500px] rounded-xl border-4 border-black" />
                    ))
                ) : mediaItems.length === 0 ? (
                    <div className="col-span-full text-center py-8">
                        <p className="text-xl text-gray-600">No media found.</p>
                    </div>
                ) : (
                    mediaItems.map((media) => {
                        const statusStyle = statusStyles[media.status] || statusStyles.queued;
                        const editState = editStates[media.id] || {};

                        return (
                            <Card
                                key={media.id}
                                className={`rounded-xl border-4 ${statusStyle.split(' ')[0]} bg-white hover:shadow-lg transition-shadow overflow-hidden flex flex-col`}
                            >
                                <div className="bg-gradient-to-br from-pink-200 to-blue-200 p-2">
                                    <div className="absolute top-2 left-2">
                                        <span className={`px-2 py-1 rounded border ${statusStyle} font-bold text-xs`}>
                                            {media.status.charAt(0).toUpperCase() + media.status.slice(1)}
                                        </span>
                                    </div>
                                    <div className="flex flex-col space-y-2">
                                        {media.original_image_url && (
                                            <img
                                                src={media.original_image_url}
                                                alt="Original"
                                                className="w-full h-48 object-contain"
                                            />
                                        )}
                                        {media.generated_image_url ? (
                                            <img
                                                src={media.generated_image_url}
                                                alt="Generated"
                                                className="w-full h-48 object-contain"
                                            />
                                        ) : (
                                            <Skeleton className="w-full h-48 rounded" />
                                        )}
                                    </div>
                                </div>
                                <CardContent className="p-4 flex-grow space-y-4">
                                    <div>
                                        <Label className="font-bold">User</Label>
                                        <p className="text-sm">{media.user_email}</p>
                                    </div>
                                    <div>
                                        <Label className="font-bold">Instructions</Label>
                                        <p className="text-sm">{media.instructions || 'None'}</p>
                                    </div>
                                    <div>
                                        <Label className="font-bold">Status</Label>
                                        <Select
                                            value={editState.status || media.status}
                                            onValueChange={(value) => handleEditChange(media.id, 'status', value)}
                                        >
                                            <SelectTrigger className="border-2 border-black rounded-xl">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="queued">Queued</SelectItem>
                                                <SelectItem value="generating">Generating</SelectItem>
                                                <SelectItem value="success">Success</SelectItem>
                                                <SelectItem value="failed">Failed</SelectItem>
                                                <SelectItem value="deleted">Deleted</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="font-bold">Generated Image</Label>
                                        <Input
                                            type="file"
                                            accept="image/*"
                                            className="border-2 border-black rounded-xl"
                                            onChange={(e) => handleGeneratedImageUpload(media.id, e.target.files[0])}
                                        />
                                        {editState.uploadProgress > 0 && (
                                            <Progress value={editState.uploadProgress} className="mt-2 border-2 border-black" />
                                        )}
                                    </div>
                                    {(editState.status === 'failed' || media.status === 'failed') && (
                                        <div>
                                            <Label className="font-bold">Failure Reason</Label>
                                            <Textarea
                                                value={editState.failure_reason || media.failure_reason || ''}
                                                onChange={(e) => handleEditChange(media.id, 'failure_reason', e.target.value)}
                                                className="border-2 border-black rounded-xl"
                                                rows={3}
                                            />
                                        </div>
                                    )}
                                    {(editState.status || editState.generated_image || editState.failure_reason) && (
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                className="border-2 border-black rounded-xl"
                                                onClick={() => cancelChanges(media.id)}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                className="bg-pink-500 text-white border-2 border-black rounded-xl hover:bg-pink-600"
                                                onClick={() => saveChanges(media.id)}
                                                disabled={actionLoading[`save_${media.id}`]}
                                            >
                                                {actionLoading[`save_${media.id}`] ? 'Saving...' : 'Save'}
                                            </Button>
                                        </div>
                                    )}
                                    <div className="text-xs text-gray-600">
                                        <p>Created: {formatDate(media.created_at)}</p>
                                        <p>Updated: {formatDate(media.updated_at)}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })
                )}
            </div>

            {mediaItems.length > 0 && (
                <div className="mt-8 flex justify-center">
                    {loading ? (
                        <div className="flex items-center gap-2">
                            <Skeleton className="w-6 h-6 rounded-full animate-spin" />
                            <span className="text-gray-600">Loading...</span>
                        </div>
                    ) : hasMore ? (
                        <Button onClick={loadMore} className="px-6 py-2 bg-pink-500 text-white font-bold rounded-xl border-2 border-black hover:bg-pink-600">
                            Load More
                        </Button>
                    ) : (
                        <p className="text-gray-600 text-lg">No more media to load!</p>
                    )}
                </div>
            )}
        </div>
    );
};

AdminMedia.propTypes = {
    initialMedia: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        original_image_url: PropTypes.string,
        generated_image_url: PropTypes.string,
        status: PropTypes.string,
        instructions: PropTypes.string,
        failure_reason: PropTypes.string,
        created_at: PropTypes.string,
        updated_at: PropTypes.string,
        user_email: PropTypes.string
    })),
    pagy: PropTypes.shape({
        page: PropTypes.number,
        next: PropTypes.number
    })
};

export default AdminMedia;